import React from 'react'
import PropTypes from 'prop-types'
import { Shield, Building2, Calendar, Clock, Mail, CheckCircle, XCircle } from 'lucide-react'
import { Badge } from '@components/common'
import { formatDate, formatRelativeTime } from '@utils'
import './ProfileAccountTab.css'

/**
 * Profile Account Tab Component
 *
 * Read-only account information display.
 * Shows role, company, permissions, and account timestamps.
 *
 * @param {Object} props - Component props
 * @param {Object} props.user - User data with role and company details
 */
const ProfileAccountTab = ({ user }) => {
	if (!user) return null

	// Format dates
	const createdAt = user.created_at ? formatDate(user.created_at, 'full') : 'Unknown'
	const lastLogin = user.last_login_at
		? formatRelativeTime(user.last_login_at)
		: 'Never'
	const emailVerified = user.email_verified_at
		? formatDate(user.email_verified_at, 'full')
		: null

	// Status variant mapping
	const statusVariants = {
		active: 'success',
		inactive: 'secondary',
		suspended: 'danger',
	}

	return (
		<div className="profile-account-tab">
			{/* Header */}
			<div className="profile-account-tab__header">
				<h3 className="profile-account-tab__title">Account Information</h3>
				<p className="profile-account-tab__subtitle">
					View your account details and access information
				</p>
			</div>

			{/* Info Sections */}
			<div className="profile-account-tab__sections">
				{/* Role & Organization */}
				<div className="profile-account-tab__section">
					<h4 className="profile-account-tab__section-title">
						<Shield size={16} />
						Role & Organization
					</h4>

					<div className="profile-account-tab__grid">
						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Role</span>
							<div className="profile-account-tab__value">
								<Badge variant="primary">{user.role_name || 'No Role'}</Badge>
							</div>
						</div>

						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Company</span>
							<div className="profile-account-tab__value profile-account-tab__value--with-icon">
								<Building2 size={16} className="profile-account-tab__icon" />
								<span>{user.company_name || 'Not assigned'}</span>
							</div>
						</div>

						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Account Status</span>
							<div className="profile-account-tab__value">
								<Badge variant={statusVariants[user.status] || 'secondary'}>
									{user.status?.charAt(0).toUpperCase() + user.status?.slice(1) || 'Unknown'}
								</Badge>
							</div>
						</div>

						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Email Verification</span>
							<div className="profile-account-tab__value profile-account-tab__value--with-icon">
								{emailVerified ? (
									<>
										<CheckCircle
											size={16}
											className="profile-account-tab__icon profile-account-tab__icon--success"
										/>
										<span>Verified on {emailVerified}</span>
									</>
								) : (
									<>
										<XCircle
											size={16}
											className="profile-account-tab__icon profile-account-tab__icon--warning"
										/>
										<span>Not verified</span>
									</>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Activity */}
				<div className="profile-account-tab__section">
					<h4 className="profile-account-tab__section-title">
						<Clock size={16} />
						Activity
					</h4>

					<div className="profile-account-tab__grid">
						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Last Login</span>
							<div className="profile-account-tab__value profile-account-tab__value--with-icon">
								<Clock size={16} className="profile-account-tab__icon" />
								<span>{lastLogin}</span>
							</div>
						</div>

						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Account Created</span>
							<div className="profile-account-tab__value profile-account-tab__value--with-icon">
								<Calendar size={16} className="profile-account-tab__icon" />
								<span>{createdAt}</span>
							</div>
						</div>

						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">User ID</span>
							<div className="profile-account-tab__value">
								<code className="profile-account-tab__code">#{user.id}</code>
							</div>
						</div>
					</div>
				</div>

				{/* Contact */}
				<div className="profile-account-tab__section">
					<h4 className="profile-account-tab__section-title">
						<Mail size={16} />
						Contact Information
					</h4>

					<div className="profile-account-tab__grid">
						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Email Address</span>
							<div className="profile-account-tab__value profile-account-tab__value--with-icon">
								<Mail size={16} className="profile-account-tab__icon" />
								<span>{user.email}</span>
							</div>
						</div>

						<div className="profile-account-tab__item">
							<span className="profile-account-tab__label">Phone Number</span>
							<div className="profile-account-tab__value">
								{user.phone || <span className="profile-account-tab__empty">Not set</span>}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Help Text */}
			<div className="profile-account-tab__help">
				<p>
					Need to change your role, email, or organization? Contact your administrator
					or support team for assistance.
				</p>
			</div>
		</div>
	)
}

ProfileAccountTab.propTypes = {
	user: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		email: PropTypes.string,
		phone: PropTypes.string,
		status: PropTypes.string,
		role_name: PropTypes.string,
		company_name: PropTypes.string,
		created_at: PropTypes.string,
		last_login_at: PropTypes.string,
		email_verified_at: PropTypes.string,
	}),
}

export default ProfileAccountTab
